import React, { useState } from 'react';
import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../../../services/api';
import { Card, Cifra, FilaCifras, COLOR, eur, pct, num, ejes, barra, useEndpoint } from '../ui';
import { CanalesCard } from '../PortfolioCards';

/**
 * Gestión: por dónde contestan los deudores, en qué intento, qué rinden las
 * llamadas y cómo acaban los planes de pago acordados.
 *
 * Las llamadas reutilizan el endpoint de llamadas (`/calls/stats/summary`), que
 * filtra por UNA empresa: con un grupo de cuentas no se puede pedir, y la card
 * lo dice en vez de enseñar la cartera entera como si fuera la del grupo.
 */
const P = '/portfolio-performance';

export default function GestionSection({ params, period, singleClient }) {
  const cp = params.clients ? { clients: params.clients } : {};
  const canales = useEndpoint(`${P}/channels`, cp);
  const intentos = useEndpoint(`${P}/wa-attempts`, cp);
  const llamadaPago = useEndpoint(`${P}/calls-to-payment`, cp);
  const planes = useEndpoint(`${P}/payment-plans`, cp);
  const llamadasPedibles = !params.clients || Boolean(singleClient);
  const llamadas = useEndpoint(
    '/calls/stats/summary',
    { from: period.desde, to: period.hasta, ...(singleClient ? { companyId: singleClient } : {}) },
    { enabled: llamadasPedibles }
  );
  const [recalculando, setRecalculando] = useState(false);

  const recalcular = async () => {
    try {
      setRecalculando(true);
      await api.post(`${P}/channels/refresh`);
      toast.info('Recalculando el embudo: tarda alrededor de un minuto.');
      setTimeout(() => {
        setRecalculando(false);
        canales.recargar();
        intentos.recargar();
        llamadaPago.recargar();
      }, 70000);
    } catch (err) {
      setRecalculando(false);
      toast.error(getErrorMessage(err));
    }
  };

  const wa = canales.data?.filas?.find(f => f.canal === 'WhatsApp');
  const ll = llamadas.data;
  const lp = llamadaPago.data;

  return (
    <div className='space-y-4'>
      <FilaCifras>
        <Cifra
          etiqueta='Responden por WhatsApp'
          cargando={canales.estado === 'loading'}
          valor={wa ? pct(wa.tasa_de_respuesta_pct) : '—'}
          pie={wa ? `${num(wa.expedientes_con_respuesta)} de ${num(wa.expedientes_contactados)} contactados` : null}
        />
        <Cifra
          etiqueta='Llamadas cogidas'
          cargando={llamadasPedibles && llamadas.estado === 'loading'}
          valor={llamadasPedibles && ll ? pct(ll.answer_rate * 100) : '—'}
          pie={
            llamadasPedibles
              ? ll
                ? `${num(ll.answered)} de ${num(ll.dials)} marcadas en el periodo`
                : null
              : 'Elige un cliente o «Todos»'
          }
        />
        <Cifra
          etiqueta='Llamadas útiles (≥45 s)'
          cargando={llamadasPedibles && llamadas.estado === 'loading'}
          valor={llamadasPedibles && ll ? pct(ll.contact_rate * 100) : '—'}
          pie={llamadasPedibles && ll ? `Duración media ${num(ll.aht_seconds)} s` : null}
        />
        <Cifra
          etiqueta='Pagan tras una llamada útil'
          cargando={llamadaPago.estado === 'loading'}
          valor={lp && !lp.pendientes ? pct(lp.pct_util_que_paga) : '—'}
          pie={
            lp
              ? lp.pendientes
                ? 'Pendiente de recalcular'
                : `${num(lp.con_pago_tras_llamada)} de ${num(lp.con_llamada_util)}, en 30 días`
              : null
          }
        />
        <Cifra
          etiqueta='Planes acordados vigentes'
          cargando={planes.estado === 'loading'}
          valor={planes.data ? num(planes.data.vigentes) : '—'}
          tono={planes.data?.vigentes_con_cuota_vencida ? 'alerta' : 'neutral'}
          pie={planes.data ? `${num(planes.data.vigentes_con_cuota_vencida)} con alguna cuota vencida` : null}
        />
      </FilaCifras>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <CanalesCard {...canales} onRecalcular={recalcular} recalculando={recalculando} />
        <IntentoCard {...intentos} />
        <LlamadasCard llamadas={llamadas} pedibles={llamadasPedibles} llamadaPago={llamadaPago} />
        <PlanesCard {...planes} />
      </div>
    </div>
  );
}

const pendienteRecalculo = n =>
  n
    ? `${num(n)} expedientes tienen el cálculo de una versión anterior, sin este dato. Se recalcula solo al desplegar; mientras tanto, «Recalcular embudo» en la card de canales.`
    : null;

function IntentoCard({ estado, data }) {
  const filas = data?.filas || [];
  const intentos = filas.filter(f => f.intento !== 'sin respuesta');
  const etiqueta = k => (k === '0' ? 'Escribió él' : k === '5+' ? '5.º o más' : `${k}.º`);
  return (
    <Card
      titulo='¿En qué intento de WhatsApp contesta el deudor?'
      lectura='De los que recibieron algún WhatsApp, el % que contesta tras el 1.º, el 2.º… Si el 3.º apenas suma, sobra.'
      nota='Intento = cuántos envíos hubo antes de su primera respuesta. «Escribió él»: respondió sin que le hubiéramos escrito. Los que nunca contestan están en la tabla.'
      estado={estado}
      vacio={!data || !data.contactados}
      pendiente={data && !data.contactados && data.pendientes ? pendienteRecalculo(data.pendientes) : null}
      filas={filas.map(f => ({
        ...f,
        intento: f.intento === 'sin respuesta' ? 'Nunca contestó' : etiqueta(f.intento),
      }))}
      csv='intento-de-whatsapp'
      columnas={[
        { key: 'intento', label: 'Contesta tras' },
        { key: 'expedientes', label: 'Expedientes', fmt: num },
        { key: 'pct', label: '% de los contactados', fmt: pct },
      ]}
    >
      <Chart
        type='bar'
        data={{
          labels: intentos.map(f => etiqueta(f.intento)),
          datasets: [
            barra(
              '% de los contactados',
              intentos.map(f => f.pct),
              COLOR.s1
            ),
          ],
        }}
        options={{
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => `${pct(ctx.parsed.y)} de los contactados` } },
          },
          scales: ejes('% de los contactados'),
        }}
      />
    </Card>
  );
}

function LlamadasCard({ llamadas, pedibles, llamadaPago }) {
  const ll = llamadas.data;
  const lp = llamadaPago.data;
  const estado = !pedibles
    ? 'ok'
    : llamadas.estado === 'ok' && llamadaPago.estado === 'ok'
      ? 'ok'
      : llamadas.estado === 'error' || llamadaPago.estado === 'error'
        ? 'error'
        : 'loading';
  const filas =
    ll && lp
      ? [
          { paso: 'Marcadas', valor: ll.dials, nota: 'En el periodo' },
          { paso: 'Cogidas', valor: ll.answered, nota: pct(ll.answer_rate * 100) },
          { paso: 'Útiles (≥45 s)', valor: ll.useful, nota: pct(ll.contact_rate * 100) },
          { paso: 'Duración media de las útiles', valor: ll.aht_seconds, nota: 'segundos' },
          { paso: 'Promesas de pago', valor: ll.promises, nota: 'Tipificadas como interesado' },
          { paso: 'Expedientes con llamada útil', valor: lp.con_llamada_util, nota: 'Toda la historia' },
          {
            paso: 'Pagan en los 30 días siguientes',
            valor: lp.con_pago_tras_llamada,
            nota: `${pct(lp.pct_util_que_paga)} · ${eur(lp.cobrado_tras_llamada)}`,
          },
        ]
      : [];
  return (
    <Card
      titulo='¿Cuántas llamadas se cogen, y cuántas acaban en pago?'
      lectura='De arriba abajo: marcadas, cogidas, útiles y las que terminan en un cobro en 30 días.'
      nota='Marcaciones del periodo (endpoint de llamadas, sin las entrantes). «Acaban en pago» mira la primera llamada útil de cada expediente y es de toda la historia. No es causal.'
      estado={estado}
      pendiente={
        !pedibles
          ? 'Las estadísticas de llamadas se piden por una empresa: elige un cliente o «Todos».'
          : lp?.pendientes && !lp?.llamados
            ? pendienteRecalculo(lp.pendientes)
            : null
      }
      vacio={!filas.length || !ll.dials}
      vacioTexto='No hay llamadas en este periodo.'
      filas={filas}
      csv='llamadas'
      columnas={[
        { key: 'paso', label: 'Paso' },
        { key: 'valor', label: 'Cuántas', fmt: num },
        { key: 'nota', label: '' },
      ]}
      alto={null}
    />
  );
}

function PlanesCard({ estado, data }) {
  const rotos = data?.rotos_por_cuota || [];
  const filas = data
    ? [
        { estado: 'Vigentes', planes: data.vigentes },
        { estado: '… con alguna cuota vencida', planes: data.vigentes_con_cuota_vencida },
        { estado: 'Cumplidos', planes: data.completados },
        { estado: 'Cancelados', planes: data.cancelados },
        { estado: '… por incumplimiento', planes: data.incumplidos },
        ...rotos.map(r => ({ estado: `Rotos en la cuota ${r.cuota}`, planes: r.planes })),
      ]
    : [];
  return (
    <Card
      titulo='¿Se cumplen los planes de pago acordados?'
      lectura='Cuántos siguen vivos, cuántos se cumplieron y, de los rotos, en qué cuota se rompieron.'
      nota={
        <>
          Solo los planes acordados con cuotas. El ritmo de los pagos parciales sin plan está en{' '}
          <a className='text-blue-600 hover:underline' href='/payment-plans'>
            Planes de pago
          </a>
          .
        </>
      }
      estado={estado}
      vacio={!data || !data.total}
      vacioTexto='No hay planes de pago acordados en esta cartera.'
      filas={filas}
      csv='planes-de-pago'
      columnas={[
        { key: 'estado', label: 'Estado' },
        { key: 'planes', label: 'Planes', fmt: num },
      ]}
      alto={null}
    />
  );
}
